// src/utils/creditHelpers.js
// ---------------------------------------------------------------------------
// Pure helpers for the Phase 6 credit-usage chat-area alert.
//
// Kept free of Chakra/react-router imports so it can be unit-tested directly
// in the CRA/jest environment (jest 27 cannot resolve react-router-dom v7 or
// Chakra-UI deep subpath imports — the pre-existing App.test.js fails the
// same way). The chat alert renders with these; tests cover them.
//
// This module touches ONLY presentation/alert logic — never ledger
// accounting, entitlement decisions, or Stripe data. It never formats or
// exposes exact dollar balances, granted/used amounts, or model/provider
// names.
// ---------------------------------------------------------------------------

// Derived usage states (client presentation only).
export const USAGE_STATE = Object.freeze({
  NORMAL: 'NORMAL', // below 90%
  CRITICAL: 'CRITICAL', // 90% <= usagePercent < 100% — dismissible inline alert
  EXHAUSTED: 'EXHAUSTED', // 100% / server reports unavailable — persistent alert
});

// Server-authoritative status values from GET /api/credit/usage.
export const USAGE_STATUS = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  EXHAUSTED: 'EXHAUSTED',
  POST_OVERRUN_BLOCKED: 'POST_OVERRUN_BLOCKED',
});

// Thresholds (product-defined; do not invent others).
export const THRESHOLDS = Object.freeze({
  CRITICAL: 90,
  EXHAUSTED: 100,
});

// sessionStorage key prefix for the SINGLE dismissible 90% alert, scoped per
// usageContext (the server-provided opaque account scope). This is the
// smallest mechanism needed for one dismissible warning — NOT the removed
// threshold-notification state machine.
export const DISMISS_90_KEY_PREFIX = 'alpherra.credit.dismiss90';

// Fallback key used only if the server omits usageContext (defensive — the
// server always sends it).
export const DISMISS_90_FALLBACK_KEY = `${DISMISS_90_KEY_PREFIX}.default`;

// Clamp a usage percentage to [0, 100].
export function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

// Derive the client presentation state from the server response.
// The server `status` is authoritative for "unavailable" — it overrides the
// percentage so the zero-balance edge (EXHAUSTED at usagePercent 0) and the
// post-overrun block are represented correctly.
export function deriveUsageState({ usagePercent, status }) {
  if (
    status === USAGE_STATUS.EXHAUSTED ||
    status === USAGE_STATUS.POST_OVERRUN_BLOCKED
  ) {
    return USAGE_STATE.EXHAUSTED;
  }
  const pct = clampPercent(usagePercent);
  if (pct >= THRESHOLDS.EXHAUSTED) return USAGE_STATE.EXHAUSTED;
  if (pct >= THRESHOLDS.CRITICAL) return USAGE_STATE.CRITICAL;
  return USAGE_STATE.NORMAL;
}

// ── 90% dismissal storage (single boolean per usageContext) ───────────────
// The ONLY client-persisted alert state. Keyed by the server-provided opaque
// usageContext so it is scoped to the current credit-allocation/account
// scope. It is cleared automatically whenever the server reports NORMAL
// (usage below 90%), which only happens when a new legitimate credit
// allocation has been granted — so a future 90% warning is re-armed for the
// new period.

// Build the sessionStorage key for the dismissible-90% flag.
export function dismiss90StorageKey(usageContext) {
  if (!usageContext) return DISMISS_90_FALLBACK_KEY;
  return `${DISMISS_90_KEY_PREFIX}.${usageContext}`;
}

// Has the user dismissed the 90% alert for this key? (default: not dismissed)
export function is90Dismissed(key, storage) {
  const store = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  if (!store) return false;
  try {
    return store.getItem(key) === '1';
  } catch {
    return false;
  }
}

// Persist a 90% dismissal for this key.
export function mark90Dismissed(key, storage) {
  const store = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  if (!store) return;
  try {
    store.setItem(key, '1');
  } catch {
    // storage unavailable (private mode / quota) — non-fatal
  }
}

// Clear a 90% dismissal for this key (new credit allocation re-arms).
export function clear90Dismissed(key, storage) {
  const store = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
}

// ── Inline chat alert copy ────────────────────────────────────────────────
// Product-friendly, percentage-based. No exact dollar values, no ledger
// terminology, no model/provider names.
export const CRITICAL_ALERT_COPY = Object.freeze({
  title: "You've used 90% or more of your available AI credits.",
  body: 'AI usage will stop when your credit is exhausted.',
});

// Exhausted-alert copy is subscription-aware (Phase 7 UX delta):
//   * ACTIVE subscription + exhausted -> credits reset next billing cycle,
//     CTA "View Subscription Details".
//   * NO active subscription + exhausted -> "Subscribe to continue".
// Both navigate to /billing. The subscription state comes from the
// server-authoritative usage response (hasActiveSubscription) — never inferred
// client-side from time, route, or refresh.
export const EXHAUSTED_ALERT_COPY = Object.freeze({
  title: 'Your AI credit has been exhausted.',
  body: 'Subscribe to continue using AI features.',
  actionLabel: 'Subscribe',
  actionHref: '/billing',
});

// Subscription-aware exhausted copy (Phase 7 UX delta).
export const EXHAUSTED_ACTIVE_SUBSCRIPTION_COPY = Object.freeze({
  title: 'Your AI credit has been exhausted.',
  body: 'Your credits will reset with your next billing cycle.',
  actionLabel: 'View Subscription Details',
  actionHref: '/billing',
});

// Resolve the exhausted-alert copy based on the server-authoritative
// subscription state. `hasActiveSubscription` comes from GET /api/credit/usage
// (never inferred client-side). Defaults to the no-subscription copy when the
// flag is absent (defensive — the server always sends it).
export function resolveExhaustedCopy(hasActiveSubscription) {
  return hasActiveSubscription
    ? EXHAUSTED_ACTIVE_SUBSCRIPTION_COPY
    : EXHAUSTED_ALERT_COPY;
}

// Resolve which inline chat alert to show above the typing area, if any.
//   * NORMAL    -> null (nothing below 90%).
//   * CRITICAL  -> { kind: 'critical' } UNLESS the user dismissed it for the
//                  current credit-allocation period.
//   * EXHAUSTED -> { kind: 'exhausted', copy } ALWAYS (persistent — no close,
//                  and independent of any 90% dismissal). `copy` is the
//                  subscription-aware exhausted copy (Phase 7 UX delta).
export function resolveChatAlert(state, is90Dismissed, hasActiveSubscription) {
  if (state === USAGE_STATE.EXHAUSTED) {
    return { kind: 'exhausted', copy: resolveExhaustedCopy(hasActiveSubscription) };
  }
  if (state === USAGE_STATE.CRITICAL && !is90Dismissed) return { kind: 'critical' };
  return null;
}

// Map a 402 response to a credit code, or null if it is not a credit 402.
export function resolveCredit402({ status, code }) {
  if (status !== 402) return null;
  if (code === 'NO_USABLE_CREDIT' || code === 'USAGE_BLOCKED_AFTER_OVERDRAFT') {
    return code;
  }
  return null;
}

// Product-friendly inline message streamed into the chat when a gated AI
// request returns 402. No dollar values, no internal details.
export const CREDIT_UNAVAILABLE_MESSAGE =
  "You've used up your available credit. Add credit or subscribe to continue using AI features.";
